import React, { useId, useState } from "react";
import { Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import SettingsOutlined from "@mui/icons-material/SettingsOutlined";
import { deploymentSelection, packageStageDeployValue, useTransportV2, vorwaertsZiele } from "../context/TransportV2Context";
import { useLang } from "../i18n";

export default function DeploymentStages({ edge = false, disabled = false, forPackage = false }) {
  const { state, dispatch } = useTransportV2();
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [values, setValues] = useState({});
  const id = useId();
  const stages = vorwaertsZiele(state);
  const plan = deploymentSelection(state);
  const field = edge ? "manualEdgeArtifacts" : "artifacts";
  const title = t(edge ? "deploy.stageEdgeTitle" : "deploy.stageCiTitle");
  const apply = () => {
    if (disabled) return;
    if (forPackage) {
      dispatch({ type: "SET_PACKAGE_DEPLOY_STAGES", payload: { values } });
    } else {
      dispatch({ type: "SET_DEPLOY_STAGES", payload: { edge, values } });
    }
    setOpen(false);
  };
  const buttonSx = { minHeight: 44, textTransform: "none", color: "var(--iio-text-1)" };
  return <>
    <Tooltip title={title}><span style={{ display: "inline-flex" }}><IconButton aria-label={title} aria-haspopup="dialog" disabled={disabled || !stages.length}
      onClick={() => { setValues({}); setOpen(true); }} sx={{ color: "var(--info)", fontSize: "inherit", width: 28, height: 44, p: 0.5 }}><SettingsOutlined sx={{ fontSize: "1em" }} /></IconButton></span></Tooltip>
    <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth aria-labelledby={id}
      PaperProps={{ className: "iio iio-transport-v2", sx: { color: "var(--iio-text-1)", background: "var(--iio-bg-1)", backgroundImage: "none",
        "&&": { minHeight: 0, height: "auto" }, border: "1px solid var(--iio-border)", borderRadius: "12px", fontFamily: '"Source Sans 3", sans-serif', m: 2, width: "calc(100% - 32px)" } }}>
      <DialogTitle id={id}>{title}</DialogTitle>
      <DialogContent>
        <Typography sx={{ mb: 2 }}>{t("deploy.stageScope", { name: state.pkgName || state.pkgId })}</Typography>
        <Stack spacing={1}>{stages.map(stage => {
          const flags = forPackage ? [] : Object.values(plan.stages?.[stage.key]?.[field] || {}).flatMap(plants => Object.values(plants));
          const edited = Object.hasOwn(values, stage.key);
          const checked = edited ? values[stage.key] : forPackage ? packageStageDeployValue(state, stage.key) : flags.length > 0 && flags.every(Boolean);
          const mixed = !edited && !forPackage && flags.some(Boolean) && !flags.every(Boolean);
          return <FormControlLabel key={stage.key} sx={{ m: 0, minHeight: 44 }} control={<Checkbox checked={checked} indeterminate={mixed} disabled={disabled}
            inputProps={{ "aria-label": stage.code || stage.key.toUpperCase() }}
            onChange={(_, value) => setValues(previous => ({ ...previous, [stage.key]: value }))} />}
            label={<Box><Typography component="span" sx={{ fontWeight: 600 }}>{stage.code || stage.key.toUpperCase()}</Typography>
              {stage.region && <Typography component="span" sx={{ ml: 1, fontWeight: 600 }}>{stage.region}</Typography>}
              <Typography component="span" sx={{ ml: 1, color: "var(--iio-text-2)" }}>{stage.name}</Typography></Box>} />;
        })}</Stack>
        <Typography sx={{ mt: 2, color: "var(--iio-text-2)" }}>{t(edge ? "deploy.stageEdgeHint" : "deploy.stageCiHint")}</Typography>
      </DialogContent>
      <DialogActions sx={{ p: 2, gap: 1, flexWrap: "wrap" }}>
        <Button sx={buttonSx} onClick={() => setOpen(false)}>{t("deploy.stageCancel")}</Button>
        <Button variant="contained" sx={{ ...buttonSx, backgroundColor: "color-mix(in srgb, var(--iio-signal) 80%, var(--ci-night))", color: "var(--ci-white)",
          "&:hover": { backgroundColor: "var(--iio-signal)" } }} disabled={disabled} onClick={apply}>{t("deploy.stageApply")}</Button>
      </DialogActions>
    </Dialog>
  </>;
}
